/*
 * Description:
 * ---------------------
 * One box's panel on the master map, opened by picking the box, one of its
 * devices or a line into it. First every device it shares, each with every
 * box allowed to reach it, the address that box uses, to copy, and how its
 * setup stands; picking one of those boxes opens that permission's details in
 * place. Then the devices its box sees that it does not share, each one Let
 * other sites reach it away, and last what devices here may reach at other boxes, each
 * opening in its own box's panel. A device's Manage access and More actions
 * give way to its permission's own while one is open. mark is what the panel
 * was opened for: a device, a rule opened, or the box a picked line comes
 * from, whose rows it marks. The panel is dragged by its head; the map keeps
 * where. Wording lives in reachWords.js.
 *
 * ! file components/BoxPanel.js
 */
import React, { useEffect, useRef, useState } from "react";
import { FiGrid, FiX } from "react-icons/fi";
import { TbRouter } from "react-icons/tb";
import { DeviceIcon, getDeviceType } from "./NetworkCanvas";
import RuleDetails, { Copyable, MoreMenu } from "./RuleDetails";
import * as W from "./reachWords";

// Devices not shared show FEW until asked for all.
const FEW = 8;

const iconOf = (r) => (r.net ? <FiGrid size={15} /> : <DeviceIcon type={getDeviceType(r.device || { hostname: r.name })} size={15} />);
const byBox = (a, b) => a.box.localeCompare(b.box);
const Address = ({ address }) => (address ? <Copyable text={address} /> : <span className="rd-quiet">{W.ADDRESS_PENDING}</span>);

export default function BoxPanel({
  site, byId, at, z, top, height, mark, inside, rules, anchors, reads, canWrite, busy,
  onGrab, onClose, onPickDevice, onPickRule, onRefresh, onFocus, onAllow, onManage, onToggle, onRemoveAccess,
}) {
  const id = site.node_id;
  const state = W.readiness(site);
  const [all, setAll] = useState(false);
  const shared = inside.rows.filter((r) => r.entry);
  const others = inside.rows.filter((r) => !r.entry);
  const reaches = rules.filter((r) => r.box === id).sort((a, b) => a.entry.name.localeCompare(b.entry.name));
  const opened = rules.find((r) => r.key === mark.rule);
  const from = mark.from ? rules.filter((r) => r.box === mark.from && r.entry.site === id) : [];
  const marked = (r) => mark.device === r.key || (!!r.entry && opened?.entry.id === r.entry.id);
  const listed = all ? others : others.filter((r, i) => i < FEW || r.key === mark.device);
  const body = useRef(null);
  // What the panel was opened for is brought into view.
  useEffect(() => {
    body.current?.querySelector(".is-on, .is-from")?.scrollIntoView?.({ block: "nearest" });
  }, [mark]);
  return (
    <section className={`rd-panel${top ? " is-top" : ""}`} style={{ left: at.x, top: at.y, zIndex: z, maxHeight: height }}
      aria-label={W.panelLabel(id)} onPointerDown={onGrab.raise}>
      <header className="rd-panel-head" onPointerDown={onGrab.drag} title={W.DRAG}>
        <span className="rd-card-icon" aria-hidden="true"><TbRouter size={17} /></span>
        <div>
          <h3>{id}</h3>
          <p><span className="rd-mono">{W.lanOf(site) || "—"}</span> · {W.READINESS[state]}</p>
        </div>
        <button type="button" className="rd-panel-close" aria-label={W.closePanel(id)} onClick={onClose}><FiX size={16} /></button>
      </header>
      <div className="rd-panel-body" ref={body}>
        {from.length > 0 && <p className="rd-panel-lead">{W.pairTitle(mark.from, id, from)}</p>}
        <section className="rd-panel-part">
          <h4>{W.SHARED_HERE} <em>{shared.length}</em></h4>
          {!shared.length && <p className="rd-quiet">{W.NOTHING_SHARED}</p>}
          {shared.map((r) => {
            const its = rules.filter((x) => x.entry.id === r.entry.id).sort(byBox);
            return (
              <article key={r.key} className={`rd-panel-device${marked(r) ? " is-on" : ""}`} aria-label={r.name}>
                <button type="button" className="rd-panel-device-head" aria-label={r.name} aria-pressed={marked(r)} onClick={() => onPickDevice(r.key)}>
                  <span className="rd-box-row-icon" aria-hidden="true">{iconOf(r)}</span>
                  <span className="rd-box-row-name">
                    <span>{r.name}</span>
                    <span className="rd-mono">{r.net ? W.everyDevice(r.ip) : `${W.LOCAL_IP} ${r.ip}`}</span>
                  </span>
                </button>
                <p className="rd-panel-caption">{W.ACCESS_FROM}</p>
                {its.length ? (
                  <ul className="rd-panel-access" aria-label={`${W.ACCESS_FROM} ${r.name}`}>
                    {its.map((rule) => {
                      const setup = W.setupOf(rule.entry, rule.box, anchors);
                      const open = opened?.key === rule.key;
                      return (
                        <li key={rule.key} aria-label={rule.box} className={`${open ? "is-open" : ""}${mark.from === rule.box ? " is-from" : ""}`}>
                          <div className="rd-panel-access-row">
                            <button type="button" className="rd-panel-pick" aria-expanded={open} onClick={() => onPickRule(rule, !open)}>{rule.box}</button>
                            <Address address={rule.address} />
                            <span className={`rd-setup is-${setup.state}`}>{W.SETUP_WORDS[setup.state]}</span>
                          </div>
                          {setup.text && !open && <small className="rd-quiet">{setup.text}</small>}
                          {open && (
                            <RuleDetails rule={rule} site={byId[rule.box]} mine={reads[rule.box]} theirs={reads[id]} anchors={anchors}
                              canWrite={canWrite} busy={busy} onRefresh={() => onRefresh([rule.box, id])} onFocus={() => onFocus(rule.box)}
                              onManage={() => onManage(rule.entry)} onToggle={() => onToggle(rule.entry)} onRemove={() => onRemoveAccess(rule)} />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : <p className="rd-quiet">{W.NO_BOX_YET}</p>}
                {canWrite && !its.includes(opened) && (
                  <div className="rd-acts">
                    <button type="button" className="dl-btn rd-small" disabled={busy} onClick={() => onManage(r.entry)}>{W.MANAGE}</button>
                    <MoreMenu disabled={busy} items={[{ label: r.entry.disabled ? W.RESUME_ALL : W.PAUSE_ALL, onPick: () => onToggle(r.entry) }]} />
                  </div>
                )}
              </article>
            );
          })}
        </section>
        <section className="rd-panel-part">
          <h4>{W.NOT_SHARED_HERE} {!inside.notes.length && <em>{others.length}</em>}</h4>
          {inside.notes.map((n) => <p key={n} className="rd-quiet">{n}</p>)}
          {others.length > 0 && (
            <ul className="rd-panel-others">
              {listed.map((r) => (
                <li key={r.key} aria-label={r.name} className={mark.device === r.key ? "is-on" : ""}>
                  <span className="rd-box-row-icon" aria-hidden="true">{iconOf(r)}</span>
                  <span className="rd-box-row-name">
                    <span>{r.name}</span>
                    <span className="rd-mono">{r.ip}</span>
                  </span>
                  {canWrite && (
                    <button type="button" className="dl-btn rd-small" disabled={busy} aria-label={W.letReach(r.name)}
                      onClick={() => onAllow({ holder: id, pick: r.key, target: r.ip, name: r.name, manage: true })}>{W.LET_REACH}</button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {others.length > FEW && (
            <button type="button" className="rd-link rd-panel-more" onClick={() => setAll(!all)}>
              {all ? W.SHOW_FEWER : W.showMore(others.length - listed.length)}
            </button>
          )}
        </section>
        <section className="rd-panel-part">
          <h4>{W.REMOTE_ACCESS} <em>{reaches.length}</em></h4>
          {reaches.length ? (
            <ul className="rd-panel-reach" aria-label={W.REMOTE_ACCESS}>
              {reaches.map((r) => {
                const setup = W.setupOf(r.entry, r.box, anchors);
                return (
                  <li key={r.key} aria-label={r.entry.name}>
                    <span className="rd-panel-reach-to">
                      <button type="button" className="rd-panel-pick" onClick={() => onPickRule(r)}>{r.entry.name}</button>
                      <small>{W.atBox(r.entry.site)}</small>
                    </span>
                    <Address address={r.address} />
                    <span className={`rd-setup is-${setup.state}`}>{W.SETUP_WORDS[setup.state]}</span>
                  </li>
                );
              })}
            </ul>
          ) : <p className="rd-quiet">{W.REACHES_NOTHING}</p>}
        </section>
      </div>
      <footer className="rd-panel-foot">
        <button type="button" className="dl-btn rd-small" onClick={() => onFocus(id)}>{W.focusOn(id)}</button>
        {canWrite && state !== "update" && (
          <button type="button" className="dl-btn rd-small" disabled={busy} onClick={() => onAllow({ source: id })}>{W.allowFrom(id)}</button>
        )}
      </footer>
    </section>
  );
}
